import { z } from 'zod';

/** BullMQ job type discriminator for the bulk photo import job. */
export const BULK_PHOTO_IMPORT_JOB_TYPE = 'bulk_photo_import' as const;

/** Maximum file size for photo bundle uploads (default 500 MB). Configurable via env var. */
export const PHOTO_BUNDLE_MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024;

/** BullMQ queue name shared between API and worker. */
export const BULK_PHOTO_IMPORT_QUEUE_NAME = 'bulk-photo-import' as const;

/**
 * Maximum number of photos allowed per media unit.
 * Shared between the single-photo upload path (apps/api) and the bulk ZIP import path (apps/worker).
 */
export const MEDIA_UNIT_MAX_PHOTOS = 10 as const;

/**
 * Maximum number of ZIP entries (photos) allowed per bulk photo import job.
 * Shared between the worker's authoritative enforcement (apps/worker) and the web upload
 * phase's client-side pre-check for direct JPG multi-select (apps/web), so the two limits
 * cannot drift apart.
 */
export const BULK_PHOTO_IMPORT_MAX_ENTRIES = 500 as const;

/** Response schema for POST /api/imports/photo-bundle */
export const PhotoBundleUploadResponseSchema = z.object({
  importJobId: z.number(),
});
export type PhotoBundleUploadResponse = z.infer<typeof PhotoBundleUploadResponseSchema>;

/**
 * Schema for individual photo result entries stored in import_jobs.errorLog.
 * Extends the base ImportErrorRow pattern by adding 'success' to severity values
 * so successful photo inserts can be surfaced in the result table.
 */
/**
 * Aggregated outcome of a photo-bundle import, carried alongside the per-photo result rows
 * so the web layer does not re-derive counts from row messages.
 */
export const BulkPhotoImportResultSummarySchema = z.object({
  /** Count of photos whose parsed supplier number matched no media unit. */
  noMediaUnitCount: z.number().int().nonnegative(),
  /**
   * Distinct supplier numbers (deduplicated, order preserved by first occurrence) that
   * matched no media unit.
   */
  unmatchedSupplierNumbers: z.array(z.string()),
});
export type BulkPhotoImportResultSummary = z.infer<typeof BulkPhotoImportResultSummarySchema>;

export const BulkPhotoImportResultRowSchema = z.object({
  /** ZIP entry index (1-based) */
  row: z.number(),
  /** Original filename */
  field: z.string(),
  /** Human-readable status/reason */
  message: z.string(),
  /** Optional machine-readable result code for UI i18n */
  code: z.string().optional(),
  /** Optional params for localized message interpolation */
  params: z.record(z.string(), z.string()).optional(),
  /** Result severity — note: 'success' added vs base ImportErrorRow */
  severity: z.enum(['success', 'warning', 'error']),
});
export type BulkPhotoImportResultRow = z.infer<typeof BulkPhotoImportResultRowSchema>;
